import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Dispatch, SetStateAction, useContext, useEffect, useState } from 'react';
import { CurrentUserContext, LoadingContext } from '../App';
import isSuperAdmin from '../hooks/isSuperAdmin';
import { failed } from '../hooks/useToast';
import { DataContext } from '../pages/TicketsPage';
import { UserDataContext, UserListContext } from '../routes/Main';
import Pills from './Pills';
import isMobile from '../hooks/isMobile';
import { catchedTrigger } from '../utils/novu';
import isDeveloper from '../hooks/isDeveloper';
import { recordLog } from '../services/recordLog';
import { ITicketData, IUserData } from '../utils/exports/Interface';

const EditPriority = (props: {
  tenant: string;
  fullId: string;
  idToChange: number | string;
  priority: string;
  fetchTicket?: () => void;
  ticketsData?: ITicketData[] | undefined;
  setTicketsData?: Dispatch<SetStateAction<ITicketData[]>> | undefined;
}) => {
  const superAdmin = isSuperAdmin();

  const developer = isDeveloper();

  const { userData } = useContext(UserDataContext);
  const { setLoading } = useContext(LoadingContext);
  const { data, setData } = useContext(DataContext);
  const { currentUser } = useContext(CurrentUserContext);

  const [open, setOpen] = useState(false);
  const [commentOpen, setCommentOpen] = useState(false);
  const [priority, setPriority] = useState('');

  const handlePriorityOpen = () => {
    setOpen(true);
  };

  const handlePriorityClose = () => {
    setOpen(false);
  };

  const handlePriorityComment = (pill: { name: string }) => {
    setCommentOpen(true);
    setPriority(pill.name);
  };

  const handleCommentClose = () => {
    setCommentOpen(false);
  };

  const priorityPills = [
    {
      name: 'HIGHEST',
      hexCode: '#E20000',
    },
    {
      name: 'HIGH',
      hexCode: '#FB7800',
    },
    {
      name: 'MEDIUM',
      hexCode: '#FEC400',
    },
    {
      name: 'LOW',
      hexCode: '#93A795',
    },
  ];

  const [priorityToChange, setPriorityToChange] = useState<number | string>();

  const handlePriorityToChange = (idToChange: number | string) => {
    setPriorityToChange(idToChange);
  };

  const [relatedUsers, setRelatedUsers] = useState<any>();

  const { userList } = useContext(UserListContext);

  useEffect(() => {
    const users = userList?.data?.users?.users?.filter(
      (user: IUserData) =>
        user?.organizations?.some((obj: { alias: string }) => obj.alias === props.tenant) ||
        user.roles[0].name === 'sa',
    );
    const notifUsers = users?.map((user: IUserData) => {
      return {
        subscriberId: JSON.stringify(user.id),
      };
    });
    setRelatedUsers(notifUsers);
  }, [userList, props.tenant]);

  const [comment, setComment] = useState('');

  const handlePriorityChange = async () => {
    setLoading(true);
    const ticketInfo = {
      priority: priority.toLowerCase(),
    };
    const messageInfo = {
      message: comment,
      sender: userData !== undefined && userData.data.name,
      ticket: priorityToChange,
      admin: (superAdmin || developer) && true,
      changed_to: priority,
    };

    await fetch(`${process.env.REACT_APP_strapiUrl}/tickets/${priorityToChange}`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${process.env.REACT_APP_token}`,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ data: ticketInfo }),
    })
      .then(async (response) => {
        if (response.status === 200) {
          await catchedTrigger('priority-change', {
            // to: {
            //   subscriberId: '63d7751916e379fe65f29506'
            // },
            to: relatedUsers,
            payload: {
              ticket: props.fullId,
              user: userData?.data?.name,
              priority: priority,
            },
          });
        }
        recordLog(priority, comment, 'ticket', props.fullId, currentUser?.id);
      })
      .catch(() => failed());

    await fetch(`${process.env.REACT_APP_strapiUrl}/chats`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${process.env.REACT_APP_token}`,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ data: messageInfo }),
    })
      .then(async (response) => {
        const responseData = await response.json();
        const updatedTickets = data.map((ticket: any) => {
          if (ticket.attributes.ticket_id === props.fullId) {
            return {
              ...ticket,
              attributes: {
                ...ticket.attributes,
                priority: responseData.data.attributes.changed_to,
              },
            };
          }
          return ticket;
        });
        const updatedBoardTickets = props.ticketsData
          ? props.ticketsData.map((ticket: any) => {
              if (ticket.attributes.ticket_id === props.fullId) {
                return {
                  ...ticket,
                  attributes: {
                    ...ticket.attributes,
                    priority: responseData.data.attributes.changed_to,
                  },
                };
              }
              return ticket;
            })
          : [];
        setData(updatedTickets);
        props.setTicketsData && props.setTicketsData(updatedBoardTickets);
        if (props.fetchTicket) {
          props.fetchTicket();
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        // failed();
      });

    // const addTicketResponse = await changePriority.json()
    //
    // const addChatResponse = await addComment.json()

    handleCommentClose();
    setComment('');
    // fetchTickets();
    // console.log(addTicketResponse);

    // console.log(addChatResponse);
  };

  const mobile = isMobile();

  return (
    <>
      <Button
        onClick={(e) => {
          e.stopPropagation();
          handlePriorityOpen();
          handlePriorityToChange(props.idToChange);
        }}
        sx={{ p: '0px', b: '0px' }}
        aria-label='handle-priority'
      >
        <Pills pill={props.priority} />
      </Button>

      <Dialog open={open} onClose={handlePriorityClose} maxWidth='xl'>
        <DialogContent>
          <Box display='flex' flexDirection='column' gap='15px'>
            <Box display='flex' justifyContent='space-between'>
              <Typography fontWeight='bold'>Change priority to:</Typography>
              <CloseIcon onClick={handlePriorityClose} cursor='pointer' />
            </Box>
            <Stack direction='row' justifyContent='space-between' gap='10px'>
              {priorityPills.map((pill) => {
                return (
                  <Button
                    key={pill.name}
                    onClick={() => {
                      handlePriorityComment(pill);
                      handlePriorityClose();
                    }}
                    sx={{ p: '0px', b: '0px' }}
                    aria-label='handle-priority-comment'
                  >
                    <Pills pill={pill.name} />
                  </Button>
                );
              })}
            </Stack>
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog open={commentOpen} onClose={handleCommentClose} fullWidth>
        <DialogContent>
          <Box display='flex' flexDirection='column' gap='15px'>
            <Box display='flex' justifyContent='space-between'>
              <Box display='flex' gap='10px'>
                <Typography fontWeight='bold'>Comment:</Typography>
                <Pills pill={priority} />
              </Box>
              <CloseIcon onClick={handleCommentClose} cursor='pointer' />
            </Box>
            <TextField
              multiline
              fullWidth
              rows={4}
              aria-label='comment'
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end' sx={{ mt: '-68px' }}>
                    <SendIcon onClick={handlePriorityChange} sx={{ cursor: 'pointer' }} />
                  </InputAdornment>
                ),
              }}
              onChange={(e) => setComment(e.target.value)}
              onKeyDown={(e) => {
                if (!mobile) {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    handlePriorityChange();
                    e.preventDefault();
                  }
                }
              }}
              value={comment}
            />
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EditPriority;
